import { useEffect, useState } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

function FloatingOrbs() {
  return (
    <>
      <div className="absolute top-[10%] left-[15%] w-64 h-64 rounded-full bg-white/[0.03] blur-3xl animate-float-1" />
      <div className="absolute bottom-[20%] right-[20%] w-72 h-72 rounded-full bg-silver-300/[0.03] blur-3xl animate-float-2" />
      <div className="absolute top-[50%] left-[50%] w-48 h-48 rounded-full bg-white/[0.02] blur-2xl animate-float-3" />
    </>
  );
}

function TechScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.04]">
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-scan-line" />
    </div>
  );
}

function TechParticles() {
  const particles = [
    { left: "12%", top: "25%", delay: "0s", size: 2 },
    { left: "28%", top: "55%", delay: "1.5s", size: 3 },
    { left: "45%", top: "35%", delay: "2.3s", size: 2 },
    { left: "58%", top: "65%", delay: "0.9s", size: 1.5 },
    { left: "72%", top: "20%", delay: "1.7s", size: 2.5 },
    { left: "88%", top: "50%", delay: "2.1s", size: 2 },
    { left: "18%", top: "75%", delay: "3.0s", size: 1.5 },
    { left: "65%", top: "80%", delay: "1.2s", size: 2 },
    { left: "82%", top: "30%", delay: "2.6s", size: 3 },
    { left: "38%", top: "88%", delay: "0.6s", size: 1.5 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/60 animate-tech-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: `${3 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function ContactHero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      <FloatingOrbs />
      <TechScanLine />
      <TechParticles />

      <div className="absolute inset-0">
        <SmartImage
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20digital%20communication%20network%20visualization%20with%20glowing%20interconnected%20nodes%20and%20flowing%20data%20streams%20on%20dark%20background%2C%20geometric%20neural%20network%20patterns%2C%20holographic%20light%20beams%20in%20silver%20white%20and%20soft%20amber%20tones%2C%20deep%20navy%20black%20background%2C%20high%20contrast%20minimalist%20technology%20art%2C%20professional%20corporate%20aesthetic%2C%20clean%20elegant%20composition&width=1600&height=900&seq=contact-hero-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center opacity-40"
          loading="eager"
          hideOnError
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 pt-32 pb-10 md:pt-40 md:pb-14">
        <div className="max-w-5xl">
          <p
            className={`text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Get in Touch
          </p>
          <h1
            className={`font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6 transition-all duration-700 delay-150 animate-text-glow ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            聯絡與支持我們
          </h1>
          <p
            className={`text-silver-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            無論您有合作想法、活動諮詢，或希望支持協會推動 AI 應用發展，我們都期待與您對話。
          </p>

          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="#contact-info"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              聯絡資訊
              <Icon className="ri-arrow-down-line" />
            </a>
            <a
              href="#support"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              支持協會
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}